import React from 'react';
import PlayerCard from './PlayerCard';
import { environment } from '../config/environment';

type Phase = 'LOBBY' | 'COUNTDOWN' | 'READING' | 'QUESTION' | 'REVEAL' | 'ACHIEVEMENTS' | 'INTERMISSION' | 'PODIUM';

interface Player {
  id: string;
  name: string;
  score: number;
}

interface GameStatusCardProps {
  quiz: {
    title: string;
  };
  gamePin: string;
  players: Player[];
  phase: Phase;
  wsConnected: boolean;
  onStartQuiz?: () => void;
  onNextQuestion?: () => void;
}

const GameStatusCard: React.FC<GameStatusCardProps> = ({
  quiz,
  gamePin,
  players,
  phase,
  wsConnected,
  onStartQuiz,
  onNextQuestion
}) => {
  // Ensure players is always an array
  const safePlayers = players ?? [];

  const getPhaseLabel = (phase: Phase) => {
    switch (phase) {
      case 'LOBBY': return 'Waiting to Start';
      case 'COUNTDOWN': return 'Starting...';
      case 'READING': return 'Reading Question';
      case 'QUESTION': return 'Answering';
      case 'REVEAL': return 'Answer Revealed';
      case 'ACHIEVEMENTS': return 'Achievements';
      case 'INTERMISSION': return 'Leaderboard';
      case 'PODIUM': return 'Game Complete';
      default: return phase;
    }
  };

  const getPhaseIcon = (phase: Phase) => {
    switch (phase) {
      case 'LOBBY': return '🎮';
      case 'COUNTDOWN': return '⏱️';
      case 'READING': return '📖';
      case 'QUESTION': return '❓';
      case 'REVEAL': return '✅';
      case 'ACHIEVEMENTS': return '🏆';
      case 'INTERMISSION': return '📊';
      case 'PODIUM': return '🎉';
      default: return '⚪';
    }
  };

  const [showAllPlayers, setShowAllPlayers] = React.useState(false);
  const displayedPlayers = showAllPlayers ? safePlayers : safePlayers.slice(0, 8);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
      {/* Quiz Title */}
      <div className="bg-orange-500 p-4">
        <h2 className="text-white font-bold text-lg text-center">{quiz.title}</h2>
      </div>

      {/* Game PIN - Small reference */}
      {phase === 'LOBBY' && (
        <div className="px-4 pt-4 pb-2 border-b border-gray-200 bg-gray-50">
          <div className="text-center">
            <p className="text-xs text-gray-600 uppercase font-semibold mb-1">PIN</p>
            <p className="text-2xl font-black text-orange-600 tracking-wider">{gamePin}</p>
          </div>
        </div>
      )}

      {/* Phase Indicator */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getPhaseIcon(phase)}</span>
            <div>
              <p className="text-xs text-gray-600 uppercase">Status</p>
              <p className="font-semibold text-gray-900">{getPhaseLabel(phase)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600 uppercase">Players</p>
            <p className="text-xl font-bold text-orange-600">{safePlayers.length}</p>
          </div>
        </div>
      </div>

      {/* Player List - Collapsible */}
      {safePlayers.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={() => setShowAllPlayers(!showAllPlayers)}
            className="w-full flex items-center justify-between mb-3 text-sm font-semibold text-gray-700 hover:text-gray-900"
          >
            <span>Players in Game</span>
            <span>{showAllPlayers ? '▲' : '▼'}</span>
          </button>

          {showAllPlayers ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {displayedPlayers.map((player) => (
                <PlayerCard key={player.id} player={player} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {displayedPlayers.map((player) => (
                <div key={player.id} className="w-10 h-10 relative">
                  <img
                    src={`${environment.api.avatar}?seed=${encodeURIComponent(player.name)}&size=40`}
                    alt={player.name}
                    className="w-full h-full rounded-full border-2 border-gray-300"
                    title={player.name}
                  />
                </div>
              ))}
              {safePlayers.length > 8 && (
                <div className="w-10 h-10 bg-gray-200 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-semibold text-gray-600">
                  +{safePlayers.length - 8}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="p-4">
        {phase === 'LOBBY' && wsConnected && (
          <button
            onClick={onStartQuiz}
            className="w-full px-4 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={safePlayers.length === 0}
          >
            Start Quiz
          </button>
        )}

        {phase === 'INTERMISSION' && (
          <button
            onClick={onNextQuestion}
            className="w-full px-4 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
          >
            Next Question
          </button>
        )}

        {phase === 'PODIUM' && (
          <div className="text-center text-sm text-gray-600">
            Game complete
          </div>
        )}
      </div>

      {/* Connection Status Indicator */}
      {!wsConnected && phase !== 'PODIUM' && (
        <div className="px-4 pb-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-center">
            <span className="text-yellow-600 font-medium">Connecting...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameStatusCard;
